import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, deleteField } from 'firebase/firestore';
import { db } from '../../../config/firebase';
import { 
  Package, Search, Plus, Edit, Trash2, X, Tag, Clock, Star,
  CheckCircle, Home, TrendingUp, Sparkles, AlertCircle, Eye, PlusCircle, Image as ImageIcon, Download
} from 'lucide-react';
import LoadingSpinner from '../../../components/LoadingSpinner';
import { useToast } from '../../../hooks/useToast';
import { ToastContainer } from '../../../components/Toast';
import { servicesToAdd, addAllServices } from '../../../utils/addServicesUpdated';

interface ServiceAddon {
  id: string;
  name: string;
  description: string;
  price: number;
  duration?: string;
  popular?: boolean;
}

export default function ServicesView() {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [services, setServices] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingService, setEditingService] = useState<any>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [addingServices, setAddingServices] = useState(false);
  const [activeTab, setActiveTab] = useState<'basic' | 'details' | 'tags' | 'addons'>('basic');
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    longDescription: '',
    categoryId: '',
    price: '',
    originalPrice: '',
    discount: '',
    duration: '',
    location: 'Himachal Pradesh',
    image: '',
    images: '',
    features: '',
    includes: '',
    popular: false,
    featured: false,
    new: false,
    trending: false,
    showOnHomePage: false,
    showInPopularSection: false,
    showInFeaturedSection: false,
    isActive: true
  });

  const [addons, setAddons] = useState<ServiceAddon[]>([]);
  const [newAddon, setNewAddon] = useState({
    name: '',
    description: '',
    price: '',
    duration: '',
    popular: false
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      const servicesSnapshot = await getDocs(collection(db, 'services'));
      const servicesData = servicesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setServices(servicesData);
      
      const categoriesSnapshot = await getDocs(collection(db, 'categories'));
      const categoriesData = categoriesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setCategories(categoriesData);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddAllServices = async () => {
    if (!confirm('This will add 35+ services to the database (5+ per category). Continue?')) {
      return;
    }
    
    try {
      setAddingServices(true);
      const result = await addAllServices();
      if (result.success) {
        toast.success(`Successfully added ${result.added} new services!`);
        await loadData();
      } else {
        toast.error('Failed to add services. Check console for details.');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to add services');
    } finally {
      setAddingServices(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.categoryId || !formData.price) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Validate price
    const priceValue = parseFloat(formData.price);
    if (isNaN(priceValue) || priceValue <= 0) {
      toast.error('Please enter a valid price greater than 0');
      return;
    }

    try {
      setActionLoading(true);
      const selectedCategory = categories.find(c => c.id === formData.categoryId);

      console.log('📝 Saving service with price:', priceValue);
      console.log('Form data price:', formData.price);

      const serviceData: any = {
        name: formData.name,
        description: formData.description,
        longDescription: formData.longDescription || formData.description,
        category: selectedCategory?.name || formData.categoryId,
        categoryId: formData.categoryId,
        provider: `RAPSAPP ${selectedCategory?.name || 'Services'}`,
        price: priceValue,
        duration: formData.duration || '1-2 hours',
        location: formData.location || 'Himachal Pradesh',
        image: formData.image || '',
        images: formData.images ? formData.images.split('\n').filter(i => i.trim()) : (formData.image ? [formData.image] : []),
        features: formData.features ? formData.features.split('\n').filter(f => f.trim()) : [],
        includes: formData.includes ? formData.includes.split('\n').filter(i => i.trim()) : [],
        popular: formData.popular || false,
        featured: formData.featured || false,
        new: formData.new || false,
        trending: formData.trending || false,
        showOnHomePage: formData.showOnHomePage || false,
        showInPopularSection: formData.showInPopularSection || false,
        showInFeaturedSection: formData.showInFeaturedSection || false,
        hasAddons: addons.length > 0,
        isActive: formData.isActive !== false,
        updatedAt: new Date().toISOString()
      };

      console.log('💾 Service data to save:', serviceData);

      // Handle optional fields - only add them if they have valid values
      if (formData.originalPrice && formData.originalPrice.trim()) {
        const originalPriceValue = parseFloat(formData.originalPrice);
        if (!isNaN(originalPriceValue) && originalPriceValue > 0) {
          serviceData.originalPrice = originalPriceValue;
          
          // Calculate discount percentage based on originalPrice and price
          if (originalPriceValue > priceValue) {
            serviceData.discount = parseFloat((((originalPriceValue - priceValue) / originalPriceValue) * 100).toFixed(1));
          }
        }
      }

      // Handle addons field - clean each addon thoroughly
      if (addons.length > 0) {
        serviceData.addons = addons.map(addon => {
          const cleanAddon: Record<string, any> = {
            id: addon.id,
            name: (addon.name || '').trim(),
            description: (addon.description || '').trim(),
            price: typeof addon.price === 'number' ? addon.price : 0
          };
          
          // Only add optional fields if they have valid values
          if (addon.duration && addon.duration.trim()) {
            cleanAddon.duration = addon.duration.trim();
          }
          if (typeof addon.popular === 'boolean') {
            cleanAddon.popular = addon.popular;
          }
          
          return cleanAddon;
        });
      }
      
      // Remove any undefined values before saving
      Object.keys(serviceData).forEach(key => {
        if (serviceData[key] === undefined) {
          delete serviceData[key];
        }
      });

      // For editing, handle field removal separately
      if (editingService) {
        // Remove originalPrice and discount if not provided
        if (!formData.originalPrice || !formData.originalPrice.trim()) {
          serviceData.originalPrice = deleteField();
          serviceData.discount = deleteField();
        }
        // Remove addons if empty
        if (addons.length === 0) {
          serviceData.addons = deleteField();
        }
      }

      console.log('📤 Final service data before save:', JSON.stringify(serviceData, null, 2));

      if (editingService) {
        console.log('🔄 Updating service with ID:', editingService.id);
        await updateDoc(doc(db, 'services', editingService.id), serviceData);
        console.log('✅ Service updated successfully in Firestore');
        toast.success('Service updated successfully!');
      } else {
        console.log('➕ Creating new service');
        console.log('📋 Form data:', JSON.stringify(formData, null, 2));
        
        // Get category info with strict validation
        const selectedCategory = categories.find(c => c.id === formData.categoryId);
        if (!selectedCategory) {
          toast.error('Invalid category selected');
          setActionLoading(false);
          return;
        }
        const categoryName = selectedCategory.name;
        
        // Validate and clean all string inputs
        const cleanName = (formData.name || '').trim();
        const cleanDescription = (formData.description || cleanName).trim();
        const cleanLongDescription = (formData.longDescription || cleanDescription).trim();
        const cleanLocation = (formData.location || '').trim() || 'Himachal Pradesh';
        const cleanDuration = (formData.duration || '').trim() || '1-2 hours';
        
        if (!cleanName) {
          toast.error('Service name is required');
          setActionLoading(false);
          return;
        }
        
        // For new services, build a completely clean object with guaranteed valid values
        const newServiceData: Record<string, any> = {
          name: cleanName,
          description: cleanDescription,
          longDescription: cleanLongDescription,
          category: categoryName,
          categoryId: formData.categoryId,
          provider: `RAPSAPP ${categoryName}`,
          price: priceValue,
          duration: cleanDuration,
          location: cleanLocation,
          rating: 5,
          reviews: 0,
          popular: Boolean(formData.popular),
          featured: Boolean(formData.featured),
          new: Boolean(formData.new),
          trending: Boolean(formData.trending),
          showOnHomePage: Boolean(formData.showOnHomePage),
          showInPopularSection: Boolean(formData.showInPopularSection),
          showInFeaturedSection: Boolean(formData.showInFeaturedSection),
          hasAddons: addons.length > 0,
          isActive: formData.isActive !== false,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
        
        console.log('🔨 Initial service data:', JSON.stringify(newServiceData, null, 2));
        
        // Add image only if provided and not empty
        if (formData.image && formData.image.trim()) {
          newServiceData.image = formData.image.trim();
        }
        
        // Add images array - ensure it's not empty
        const imagesList = formData.images 
          ? formData.images.split('\n').filter(i => i.trim()).map(i => i.trim())
          : (formData.image && formData.image.trim() ? [formData.image.trim()] : []);
        
        if (imagesList.length > 0) {
          newServiceData.images = imagesList;
        }
        
        // Add features array - ensure it's not empty
        const featuresList = formData.features 
          ? formData.features.split('\n').filter(f => f.trim()).map(f => f.trim())
          : [];
        
        if (featuresList.length > 0) {
          newServiceData.features = featuresList;
        }
        
        // Add includes array - ensure it's not empty
        const includesList = formData.includes 
          ? formData.includes.split('\n').filter(i => i.trim()).map(i => i.trim())
          : [];
        
        if (includesList.length > 0) {
          newServiceData.includes = includesList;
        }
        
        // Add optional price fields only if they have valid values
        if (formData.originalPrice && formData.originalPrice.trim()) {
          const originalPriceValue = parseFloat(formData.originalPrice);
          if (!isNaN(originalPriceValue) && originalPriceValue > 0) {
            newServiceData.originalPrice = originalPriceValue;
            
            // Calculate discount percentage
            if (originalPriceValue > priceValue) {
              newServiceData.discount = parseFloat((((originalPriceValue - priceValue) / originalPriceValue) * 100).toFixed(1));
            }
          }
        }
        
        // Add addons if present - clean each addon thoroughly
        if (addons.length > 0) {
          newServiceData.addons = addons.map(addon => {
            const cleanAddon: Record<string, any> = {
              id: addon.id,
              name: (addon.name || '').trim(),
              description: (addon.description || '').trim(),
              price: typeof addon.price === 'number' ? addon.price : 0
            };
            
            // Only add optional fields if they have valid values
            if (addon.duration && addon.duration.trim()) {
              cleanAddon.duration = addon.duration.trim();
            }
            if (typeof addon.popular === 'boolean') {
              cleanAddon.popular = addon.popular;
            }
            
            return cleanAddon;
          });
        }
        
        // CRITICAL: Final cleanup - remove any undefined, null, empty string, or empty array values
        const cleanData: Record<string, any> = {};
        Object.keys(newServiceData).forEach(key => {
          const value = newServiceData[key];
          // Skip undefined, null, empty strings
          if (value === undefined || value === null || value === '') {
            console.log(`❌ Skipping field "${key}" with value:`, value);
            return;
          }
          // Skip empty arrays
          if (Array.isArray(value) && value.length === 0) {
            console.log(`❌ Skipping empty array field "${key}"`);
            return;
          }
          // Include valid values
          console.log(`✅ Including field "${key}" with value:`, value);
          cleanData[key] = value;
        });
        
        console.log('📝 Final clean service data:', JSON.stringify(cleanData, null, 2));
        console.log('🔍 Checking for undefined values...');
        
        // Extra safety check - scan for any undefined values
        let hasUndefined = false;
        Object.keys(cleanData).forEach(key => {
          if (cleanData[key] === undefined) {
            console.error(`❌❌❌ FOUND UNDEFINED in field: ${key}`);
            hasUndefined = true;
            delete cleanData[key];
          }
        });
        
        if (hasUndefined) {
          console.error('⚠️ Had to remove undefined values - final data:', JSON.stringify(cleanData, null, 2));
        }
        
        console.log('🚀 Attempting to save to Firebase...');
        const docRef = await addDoc(collection(db, 'services'), cleanData);
        console.log('✅✅✅ New service created successfully with ID:', docRef.id);
        toast.success('Service added successfully!');
      }

      await loadData();
      setShowModal(false);
      resetForm();
    } catch (error) {
      console.error('Error saving service:', error);
      toast.error('Failed to save service: ' + (error as Error).message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleEdit = (service: any) => {
    setEditingService(service);
    setFormData({
      name: service.name || '',
      description: service.description || '',
      longDescription: service.longDescription || service.description || '',
      categoryId: service.categoryId || service.category || '',
      price: service.price?.toString() || '',
      originalPrice: service.originalPrice?.toString() || '',
      discount: service.discount?.toString() || '',
      duration: service.duration || '',
      location: service.location || 'Himachal Pradesh',
      image: service.image || '',
      images: Array.isArray(service.images) ? service.images.join('\n') : '',
      features: Array.isArray(service.features) ? service.features.join('\n') : '',
      includes: Array.isArray(service.includes) ? service.includes.join('\n') : '',
      popular: service.popular || false,
      featured: service.featured || false,
      new: service.new || false,
      trending: service.trending || false,
      showOnHomePage: service.showOnHomePage || false,
      showInPopularSection: service.showInPopularSection || false,
      showInFeaturedSection: service.showInFeaturedSection || false,
      isActive: service.isActive !== false
    });
    
    setAddons(service.addons || []);
    setShowModal(true);
  };

  const handleDelete = async (serviceId: string) => {
    if (!confirm('Are you sure you want to delete this service?')) {
      return;
    }

    try {
      setActionLoading(true);
      await deleteDoc(doc(db, 'services', serviceId));
      await loadData();
      toast.success('Service deleted successfully!');
    } catch (error) {
      console.error('Error deleting service:', error);
      toast.error('Failed to delete service');
    } finally {
      setActionLoading(false);
    }
  };

  const resetForm = () => {
    setEditingService(null);
    setFormData({
      name: '',
      description: '',
      longDescription: '',
      categoryId: '',
      price: '',
      originalPrice: '',
      discount: '',
      duration: '',
      location: 'Himachal Pradesh',
      image: '',
      images: '',
      features: '',
      includes: '',
      popular: false,
      featured: false,
      new: false,
      trending: false,
      showOnHomePage: false,
      showInPopularSection: false,
      showInFeaturedSection: false,
      isActive: true
    });
    setAddons([]);
    setNewAddon({
      name: '',
      description: '',
      price: '',
      duration: '',
      popular: false
    });
    setActiveTab('basic');
  };

  const handleAddAddon = () => {
    if (!newAddon.name || !newAddon.price) {
      toast.error('Please fill addon name and price');
      return;
    }

    const addon: ServiceAddon = {
      id: Date.now().toString(),
      name: newAddon.name,
      description: newAddon.description,
      price: parseFloat(newAddon.price),
      duration: newAddon.duration || undefined,
      popular: newAddon.popular
    };

    setAddons([...addons, addon]);
    setNewAddon({
      name: '',
      description: '',
      price: '',
      duration: '',
      popular: false
    });
  };

  const filteredServices = services.filter((service: any) => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      service.name?.toLowerCase().includes(search) ||
      service.category?.toLowerCase().includes(search) ||
      service.description?.toLowerCase().includes(search)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <>
      <ToastContainer toasts={toast.toasts} removeToast={toast.removeToast} />
      <div className="space-y-6 p-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-xl border border-blue-100">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <div className="p-2 bg-blue-600 rounded-lg">
                <Package className="h-7 w-7 text-white" />
              </div>
              Services Management
            </h1>
            <p className="text-gray-600 mt-2 ml-14">Create, edit, and manage your service offerings</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleAddAllServices}
              disabled={addingServices}
              className="px-4 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-xl hover:from-green-700 hover:to-green-800 font-semibold flex items-center gap-2 shadow-lg hover:shadow-xl transition-all disabled:opacity-50"
            >
              <Download className="h-5 w-5" />
              {addingServices ? 'Adding...' : 'Add 35+ Services'}
            </button>
            <button
              onClick={() => {
                resetForm();
                setShowModal(true);
              }}
              className="px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 font-semibold flex items-center gap-2 shadow-lg hover:shadow-xl transition-all"
            >
              <Plus className="h-5 w-5" />
              Add New Service
            </button>
          </div>
        </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
        <input
          type="text"
          placeholder="Search services by name, category, or description..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
        />
      </div>

      {/* Services Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Service
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Price
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Duration
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Rating
                </th>
                <th className="px-6 py-4 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Popular
                </th>
                <th className="px-6 py-4 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {filteredServices.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12">
                    <div className="text-center">
                      <Package className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">No services found</h3>
                      <p className="text-gray-600">
                        {searchTerm ? 'Try adjusting your search' : 'Click "Add New Service" to get started'}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredServices.map((service: any) => (
                  <motion.tr
                    key={service.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="hover:bg-blue-50/50 transition-colors"
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-4">
                        {service.image && (
                          <img
                            src={service.image}
                            alt={service.name}
                            className="h-12 w-12 rounded-lg object-cover ring-2 ring-gray-100"
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                            }}
                          />
                        )}
                        <div>
                          <div className="font-semibold text-gray-900">{service.name}</div>
                          <div className="text-sm text-gray-500 line-clamp-1">{service.description}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                        <Tag className="h-3 w-3" />
                        {service.category}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-1 text-sm font-bold text-green-700">
                          ₹{service.price}
                        </div>
                        {service.originalPrice && service.originalPrice > service.price && (
                          <>
                            <div className="flex items-center gap-1 text-xs text-gray-400 line-through">
                              ₹{service.originalPrice}
                            </div>
                            <div className="inline-flex items-center gap-1 text-xs font-medium text-red-600">
                              {(((service.originalPrice - service.price) / service.originalPrice) * 100).toFixed(1)}% OFF
                            </div>
                          </>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1.5 text-sm text-gray-600">
                        <Clock className="h-4 w-4 text-blue-500" />
                        {service.duration}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1.5 text-sm">
                        <Star className="h-4 w-4 text-yellow-400 fill-yellow-400" />
                        <span className="font-medium text-gray-900">{service.rating}</span>
                        <span className="text-gray-500">({service.reviews || 0})</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col items-center gap-1">
                        {service.showInPopularSection || service.popular ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-yellow-100 to-amber-100 text-yellow-700 border border-yellow-200">
                            <Star className="h-3 w-3 fill-yellow-500" />
                            Popular
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">-</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => handleEdit(service)}
                          className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                          title="Edit service"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(service.id)}
                          disabled={actionLoading}
                          className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors disabled:opacity-50"
                          title="Delete service"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Service Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => {
              setShowModal(false);
              resetForm();
            }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-2xl font-bold text-gray-900">
                  {editingService ? 'Edit Service' : 'Add New Service'}
                </h2>
              </div>

              {/* Tabs */}
              <div className="flex border-b border-gray-200 px-6">
                <button type="button" onClick={() => setActiveTab('basic')} className={`px-4 py-3 font-medium text-sm border-b-2 transition-colors ${activeTab === 'basic' ? 'border-primary-600 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>Basic Info</button>
                <button type="button" onClick={() => setActiveTab('details')} className={`px-4 py-3 font-medium text-sm border-b-2 transition-colors ${activeTab === 'details' ? 'border-primary-600 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>Details & Content</button>
                <button type="button" onClick={() => setActiveTab('tags')} className={`px-4 py-3 font-medium text-sm border-b-2 transition-colors ${activeTab === 'tags' ? 'border-primary-600 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>Tags & Display</button>
                <button type="button" onClick={() => setActiveTab('addons')} className={`px-4 py-3 font-medium text-sm border-b-2 transition-colors ${activeTab === 'addons' ? 'border-primary-600 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>Add-ons</button>
              </div>

              <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
                <div className="flex-1 overflow-y-auto p-6">
                  {/* Basic Info Tab */}
                  {activeTab === 'basic' && (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Service Name *</label>
                        <input type="text" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" required />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Short Description</label>
                        <textarea value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" placeholder="Brief description for cards and listings" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
                        <select value={formData.categoryId} onChange={e => setFormData({ ...formData, categoryId: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" required>
                          <option value="">Select a category</option>
                          {categories.map(cat => (<option key={cat.id} value={cat.id}>{cat.name}</option>))}
                        </select>
                      </div>
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Price (₹) *</label>
                          <input
                            type="number"
                            value={formData.price}
                            onChange={e => setFormData({ ...formData, price: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            placeholder="e.g., 499"
                            min="0"
                            step="0.01"
                            required
                          />
                          <p className="text-xs text-gray-500 mt-1">Final selling price</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Original Price (₹)</label>
                          <input 
                            type="number" 
                            value={formData.originalPrice} 
                            onChange={e => setFormData({ ...formData, originalPrice: e.target.value })} 
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" 
                            placeholder="e.g., 599"
                            min="0"
                            step="0.01"
                          />
                          <p className="text-xs text-gray-500 mt-1">For discount display</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Discount (%)</label>
                          <input 
                            type="number" 
                            value={
                              formData.originalPrice && formData.price && parseFloat(formData.originalPrice) > parseFloat(formData.price)
                                ? (((parseFloat(formData.originalPrice) - parseFloat(formData.price)) / parseFloat(formData.originalPrice)) * 100).toFixed(1)
                                : ''
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 focus:ring-2 focus:ring-primary-500 focus:border-transparent" 
                            placeholder="Auto-calculated"
                            readOnly
                          />
                          <p className="text-xs text-gray-500 mt-1">Auto-calculated from prices</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Duration *</label>
                          <select 
                            value={formData.duration} 
                            onChange={e => setFormData({ ...formData, duration: e.target.value })} 
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            required
                          >
                            <option value="">Select duration</option>
                            <optgroup label="Hourly Based">
                              <option value="30 minutes">30 minutes</option>
                              <option value="1 hour">1 hour</option>
                              <option value="1-2 hours">1-2 hours</option>
                              <option value="2-3 hours">2-3 hours</option>
                              <option value="3-4 hours">3-4 hours</option>
                              <option value="4-5 hours">4-5 hours</option>
                              <option value="5-6 hours">5-6 hours</option>
                            </optgroup>
                            <optgroup label="Single Day">
                              <option value="Half day">Half day</option>
                              <option value="Full day">Full day</option>
                              <option value="1 day">1 day (no overnight)</option>
                            </optgroup>
                            <optgroup label="Multi-Day with Nights">
                              <option value="2 days, 1 night">2 days, 1 night</option>
                              <option value="3 days, 2 nights">3 days, 2 nights</option>
                              <option value="4 days, 3 nights">4 days, 3 nights</option>
                              <option value="5 days, 4 nights">5 days, 4 nights</option>
                              <option value="6 days, 5 nights">6 days, 5 nights</option>
                              <option value="7 days, 6 nights">7 days, 6 nights</option>
                            </optgroup>
                            <optgroup label="Weekly Packages">
                              <option value="1 week (7 days, 6 nights)">1 week (7 days, 6 nights)</option>
                              <option value="10 days, 9 nights">10 days, 9 nights</option>
                              <option value="2 weeks (14 days, 13 nights)">2 weeks (14 days, 13 nights)</option>
                              <option value="3 weeks (21 days, 20 nights)">3 weeks (21 days, 20 nights)</option>
                            </optgroup>
                            <optgroup label="Monthly">
                              <option value="1 month">1 month</option>
                              <option value="2 months">2 months</option>
                              <option value="3 months">3 months</option>
                              <option value="6 months">6 months</option>
                            </optgroup>
                            <optgroup label="One-Time / Variable">
                              <option value="One-time service">One-time service</option>
                              <option value="As needed">As needed</option>
                              <option value="Varies">Varies</option>
                              <option value="Customizable">Customizable</option>
                            </optgroup>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                          <input type="text" value={formData.location} onChange={e => setFormData({ ...formData, location: e.target.value })} placeholder="e.g., Delhi NCR" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Main Image URL *</label>
                        <input type="url" value={formData.image} onChange={e => setFormData({ ...formData, image: e.target.value })} placeholder="https://example.com/image.jpg" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" required />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Additional Images (one per line)</label>
                        <textarea value={formData.images} onChange={e => setFormData({ ...formData, images: e.target.value })} rows={3} placeholder="https://example.com/image1.jpg&#10;https://example.com/image2.jpg" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" />
                      </div>
                    </div>
                  )}
                  {/* Details Tab */}
                  {activeTab === 'details' && (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Long Description</label>
                        <textarea value={formData.longDescription} onChange={e => setFormData({ ...formData, longDescription: e.target.value })} rows={6} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" placeholder="Detailed description of the service..." />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Features (one per line)</label>
                        <textarea value={formData.features} onChange={e => setFormData({ ...formData, features: e.target.value })} rows={5} placeholder="Professional service&#10;Quality materials&#10;Satisfaction guaranteed" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">What's Included (one per line)</label>
                        <textarea value={formData.includes} onChange={e => setFormData({ ...formData, includes: e.target.value })} rows={5} placeholder="Service inspection&#10;All necessary tools&#10;Cleanup after work" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" />
                      </div>
                    </div>
                  )}
                  {/* Tags & Display Tab */}
                  {activeTab === 'tags' && (
                    <div className="space-y-6">
                      {/* Service Visibility Status */}
                      <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4">
                        <div className="flex items-start gap-3">
                          <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                          <div className="flex-1">
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">Service Visibility</h3>
                            <label className="flex items-center p-4 bg-white border-2 border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                              <input 
                                type="checkbox" 
                                checked={formData.isActive} 
                                onChange={e => setFormData({ ...formData, isActive: e.target.checked })} 
                                className="w-5 h-5 text-green-600 rounded focus:ring-2 focus:ring-green-500" 
                              />
                              <div className="ml-3">
                                <span className="text-base font-semibold text-gray-900">Service is Active</span>
                                <p className="text-sm text-gray-600 mt-0.5">
                                  {formData.isActive 
                                    ? '✅ This service is VISIBLE to customers' 
                                    : '❌ This service is HIDDEN from customers'}
                                </p>
                              </div>
                            </label>
                            <p className="text-xs text-blue-700 mt-2">
                              💡 Uncheck this to temporarily hide the service without deleting it
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Service Tags */}
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Service Tags</h3>
                        <p className="text-sm text-gray-600 mb-3">Add badges to highlight this service</p>
                        <div className="grid grid-cols-2 gap-3">
                          <label className="flex items-center p-3 border-2 border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                            <input type="checkbox" checked={formData.popular} onChange={e => setFormData({ ...formData, popular: e.target.checked })} className="w-4 h-4 text-primary-600 rounded focus:ring-2 focus:ring-primary-500" />
                            <span className="ml-2 text-sm font-medium text-gray-700">⭐ Popular</span>
                          </label>
                          <label className="flex items-center p-3 border-2 border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                            <input type="checkbox" checked={formData.new} onChange={e => setFormData({ ...formData, new: e.target.checked })} className="w-4 h-4 text-primary-600 rounded focus:ring-2 focus:ring-primary-500" />
                            <span className="ml-2 text-sm font-medium text-gray-700">🆕 New</span>
                          </label>
                          <label className="flex items-center p-3 border-2 border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                            <input type="checkbox" checked={formData.trending} onChange={e => setFormData({ ...formData, trending: e.target.checked })} className="w-4 h-4 text-primary-600 rounded focus:ring-2 focus:ring-primary-500" />
                            <span className="ml-2 text-sm font-medium text-gray-700">🔥 Trending</span>
                          </label>
                          <label className="flex items-center p-3 border-2 border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                            <input type="checkbox" checked={formData.featured} onChange={e => setFormData({ ...formData, featured: e.target.checked })} className="w-4 h-4 text-primary-600 rounded focus:ring-2 focus:ring-primary-500" />
                            <span className="ml-2 text-sm font-medium text-gray-700">⚡ Featured</span>
                          </label>
                        </div>
                      </div>

                      {/* Homepage Display Options */}
                      <div className="border-t border-gray-200 pt-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Homepage Display</h3>
                        <p className="text-sm text-gray-600 mb-3">Control where this service appears on the homepage</p>
                        <div className="space-y-3">
                          <label className="flex items-start p-3 border-2 border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                            <input type="checkbox" checked={formData.showInPopularSection} onChange={e => setFormData({ ...formData, showInPopularSection: e.target.checked })} className="w-4 h-4 text-primary-600 rounded focus:ring-2 focus:ring-primary-500 mt-1" />
                            <div className="ml-3">
                              <span className="text-sm font-medium text-gray-900">Show in Popular Services Section</span>
                              <p className="text-xs text-gray-500 mt-0.5">Display in the "Popular Services" carousel on homepage</p>
                            </div>
                          </label>
                        </div>
                      </div>
                    </div>
                  )}
                  {/* Add-ons Tab */}
                  {activeTab === 'addons' && (
                    <div className="space-y-6">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-3">Service Add-ons</h3>
                        {addons.length === 0 ? (
                          <p className="text-gray-500 text-sm">No add-ons added yet.</p>
                        ) : (
                          <div className="space-y-2">
                            {addons.map((addon, index) => (
                              <div key={index} className="flex items-center justify-between p-3 border border-gray-300 rounded-lg">
                                <div>
                                  <p className="font-medium text-gray-900">{addon.name}</p>
                                  <p className="text-sm text-gray-500">{addon.description}</p>
                                </div>
                                <div className="flex items-center gap-3">
                                  <span className="font-semibold text-primary-600">₹{addon.price}</span>
                                  <button type="button" onClick={() => setAddons(addons.filter((_, i) => i !== index))} className="text-red-600 hover:text-red-700">Remove</button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="border-t border-gray-200 pt-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-3">Add New Add-on</h3>
                        <div className="space-y-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Add-on Name</label>
                            <input type="text" value={newAddon.name} onChange={e => setNewAddon({ ...newAddon, name: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" placeholder="e.g., Extra cleaning" />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                            <input type="text" value={newAddon.description} onChange={e => setNewAddon({ ...newAddon, description: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" placeholder="Brief description" />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Price (₹)</label>
                            <input type="number" value={newAddon.price} onChange={e => setNewAddon({ ...newAddon, price: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" placeholder="0" />
                          </div>
                          <button type="button" onClick={handleAddAddon} className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium transition-colors">Add Add-on</button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex gap-3 p-6 border-t border-gray-200">
                  <button type="button" onClick={() => { setShowModal(false); resetForm(); }} className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium transition-colors">Cancel</button>
                  <button type="submit" disabled={actionLoading} className="flex-1 px-4 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors">{actionLoading ? 'Saving...' : editingService ? 'Update Service' : 'Add Service'}</button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      </div>
    </>
  );
}
